import {IPortalseite, ISchueler_testsAPI, TFach} from "../Interfaces";
import Veraportal from "./../Veraportal";
import Utilities from "./../Utilities";
import {IDomaenenDB, IGruppenDB, ISchuelerDB} from "../InterfacesDB";
import FachGruppeFilter from "./../FachGruppeFilter";
import Statistik from './../Statistik';

const RUECKMELDUNG_STATISTIKNAMEN: Readonly<Record<string, string>> = {
    'rueckmeldung-lh-schuelerebene-page': 'rueckmeldung-lh-schueler',
    'rueckmeldung-lh-aufgabenebene-page': 'rueckmeldung-lh-aufgaben',
    'rueckmeldung-lh-fluss-page': 'rueckmeldung-lh-fluss',
    'rueckmeldung-ks-schuelerebene-page': 'rueckmeldung-ks-schueler'
};

export default class Rueckmeldung extends Veraportal implements IPortalseite {
    protected rueckmeldungContainer: HTMLDivElement;
    protected rmFilter: FachGruppeFilter;
    protected gruppeAngezeigt: IGruppenDB;
    protected schuelerInGruppe: ISchuelerDB[];
    protected testsInGruppe: ISchueler_testsAPI[];
    protected testsInSchule: ISchueler_testsAPI[];
    protected domaenenInGruppe: IDomaenenDB[] = [];
    protected isRnfAnsichtEnabled: boolean = true;
    private balkensegmente: HTMLElement[] = [];
    private balkenResizeObserver: ResizeObserver | null = null;
    private readonly aktualisiereBalkenbeschriftungen = (): void => {
        this.balkensegmente.forEach(segment => {
            const beschriftung = segment.querySelector<HTMLElement>('.lh-balken-prozentwert');
            if (beschriftung === null)
                return;

            const abstand = parseFloat(getComputedStyle(segment).fontSize) * .6;
            segment.classList.toggle('lh-beschriftung-sichtbar', beschriftung.getBoundingClientRect().width + abstand <= segment.getBoundingClientRect().width);
        });
    };

    override async onSeiteLoad(): Promise<boolean> {
        this.rueckmeldungContainer = document.querySelector(`div.rueckmeldung-lh-aufgabenebene, div.rueckmeldung-lh-schuelerebene, div#lh-fluss-container, div.rueckmeldung-ks-schuelerebene, div#rueckmeldung-ks-vergleich, div.rueckmeldung-veracheck`) ?? null;

        this.isRnfAnsichtEnabled = ((document.querySelector(`#rnf-ansicht-checkbox`) as HTMLInputElement)?.checked === true);

        document.dispatchEvent(new Event('refresh-rueckmeldung'));

        return true;
    }

    override async onSeiteFinished(): Promise<boolean> {
        return true;
    }

    override async onSeiteLeave(): Promise<boolean> {
        this.balkenResizeObserver?.disconnect();
        this.balkenResizeObserver = null;
        this.balkensegmente = [];
        window.removeEventListener('beforeprint', this.aktualisiereBalkenbeschriftungen);
        window.removeEventListener('afterprint', this.aktualisiereBalkenbeschriftungen);
        return true;
    }

    /** Übernimmt nach dem Rendern die Segmente und zeigt nur vollständig passende Prozentbeschriftungen */
    protected refreshBalkenbeschriftungen(): void {
        if (this.balkenResizeObserver === null) {
            this.balkenResizeObserver = new ResizeObserver(this.aktualisiereBalkenbeschriftungen);
            window.addEventListener('beforeprint', this.aktualisiereBalkenbeschriftungen);
            window.addEventListener('afterprint', this.aktualisiereBalkenbeschriftungen);
        }

        this.balkenResizeObserver.disconnect();
        this.balkensegmente = Array.from(this.rueckmeldungContainer.querySelectorAll<HTMLElement>('.lh-balkenspur > span'));
        this.balkensegmente.forEach(segment => this.balkenResizeObserver?.observe(segment));
        this.aktualisiereBalkenbeschriftungen();
    }

    protected async filterAnwenden(): Promise<boolean> {

        if (this.sessionvariablen.user.accounttyp !== 5)
            this.rmFilter = new FachGruppeFilter(this, this.gruppenliste, this.sessionvariablen.get['fach'] ?? null, this.sessionvariablen.get['gruppeid'] ?? null);
        else
            this.rmFilter = new FachGruppeFilter(
                this,
                this.gruppenliste,
                this.gruppenliste.filter(g => g.gruppe_id === this.sessionvariablen.user.gruppe_id)[0]?.kurs_fach as TFach,
                this.sessionvariablen.user.gruppe_id, true
            );

        this.rmFilter.setGruppeFach();

        if (this.sessionvariablen.schule.land === 'ni')
        {
            if (['ma','de','en','fr'].includes(this.rmFilter.fach as any)) {
                this.sessionvariablen.get['fach'] = this.rmFilter.fach;
            }

            if (this.rmFilter.gruppeId != null) {
                this.sessionvariablen.get['gruppeid'] = this.rmFilter.gruppeId;
            }
        }

        if (this.rmFilter.gruppeId === null)
            return false;

        this.gruppeAngezeigt = this.gruppenliste.filter(g => g.gruppe_id === this.rmFilter.gruppeId)[0] ?? null;

        this.schuelerInGruppe = this.schuelerliste
            .filter(
                s =>
                    this.schuelerGruppenZuweisungen
                        .filter(x => x.gruppe_id === this.rmFilter.gruppeId)
                        .map(x => x.schueler_id)
                        .includes(s.schueler_id) &&
                    s.is_deaktiviert === 0
            );
        if (this.schuelerInGruppe.length < 1 && this.rueckmeldungContainer !== null) {
            this.rueckmeldungContainer.innerHTML =
                `Keine Schüler ${(this.gruppeAngezeigt.typ === 'STAMMKLASSE') ? `der Stammklasse` : `des Kurses`} <i>${this.gruppeAngezeigt.bezeichnung}</i> haben ein Testheft
                im Fach ${Utilities.printFachLang(this.rmFilter.fach)} zugewiesen.`;
            return false;
        }

        this.testsInGruppe = this.testliste
            .filter(
                t => {
                    return (
                        this.schuelerInGruppe.map(s => s.schueler_id).includes(t.schueler_id) &&
                        (this.rmFilter.fach === 'alle' || t.fach === this.rmFilter.fach)
                    )
                }
            ) ?? [];

        this.testsInSchule = this.testliste
            .filter(
                t => {
                    return (
                        (this.rmFilter.fach === 'alle' || t.fach === this.rmFilter.fach)
                    )
                }
            ) ?? [];


        // welche Domänen kommen in den angezeigten Tests vor / unique durch Set-Konvertierung
        this.domaenenInGruppe = new Array(...new Set(
            // auf Itemebene - in Mathematik mit allen Domänen im Basismodul
            this.testheftliste
                .filter(
                    t => this.testsInGruppe
                        .map(tg => tg.testheft_id)
                        .includes(t.testheft_id)
                )
                .flatMap(t => t.module
                    .flatMap(m => m.aufgaben
                        .flatMap(a => a.items
                            .flatMap(i => i.domaene)
                        )
                    )
                )
                .map(ds => this.domaenenliste.filter(d => d.domaene === ds)[0])



            // auf Modulebene - in Mathematik nur ma-gl im Basismodul
            // this.testheftliste
            //     .filter(
            //         t => this.testsInGruppe
            //             .map(tg => tg.testheft_id)
            //             .includes(t.testheft_id)
            //     )
            //     .flatMap(t => t.module.flatMap(m => m.domaene))
            //     .map(ds => this.domaenenliste.filter(d => d.domaene === ds)[0])
        )) ?? [];


        let hauptLeitideeMathe = 'ma-dz'; // fallback

        if (this.rmFilter.fach === 'ma' && this.testsInGruppe.length > 0) {
            const testheftId = this.testsInGruppe[0].testheft_id;
            const testheft = this.testheftliste.filter(t => t.testheft_id === testheftId)[0] ?? null;

            if (testheft !== null) {
                const kurz = (testheft.bezeichnung_kurz ?? '').toLowerCase();
                const lang = (testheft.bezeichnung ?? '').toLowerCase();

                if (kurz.includes('dz') || lang.includes('daten') || lang.includes('zufall')) {
                    hauptLeitideeMathe = 'ma-dz';
                } else if (kurz.includes('fz') || lang.includes('funktionaler')) {
                    hauptLeitideeMathe = 'ma-fz';
                } else if (kurz.includes('me') || lang.includes('messen') || lang.includes('größen')) {
                    hauptLeitideeMathe = 'ma-me';
                }
            }
        }

        let domaenenReihenfolge = [hauptLeitideeMathe, 'de-le', 'de-zu', 'en-le', 'en-zu', 'fr-le', 'fr-zu']; //TODO:low hardgecoded muss angepasst werden - vielleicht Domaenentabelle
        this.domaenenInGruppe
            .sort((a, b) => {
                const indexA = domaenenReihenfolge.indexOf(a.domaene);
                const indexB = domaenenReihenfolge.indexOf(b.domaene);

                // wenn eine unspezifizierte Domäne in der Liste ist, diese ans Ende setzen
                const orderA = indexA === -1 ? 999 : indexA;
                const orderB = indexB === -1 ? 999 : indexB;

                return orderA - orderB;
            });
        if (this.testsInGruppe.length < 1 && this.rueckmeldungContainer !== null) {
            this.rueckmeldungContainer.innerHTML =
                `Keine Schüler ${(this.gruppeAngezeigt.typ === 'STAMMKLASSE') ? `der Stammklasse` : `des Kurses`} <i>${this.gruppeAngezeigt.bezeichnung}</i> haben einen Test
                im Fach ${Utilities.printFachLang(this.rmFilter.fach)} zugewiesen.`;
            return false;
        }

        const statistikname = RUECKMELDUNG_STATISTIKNAMEN[document.body.id] ?? null;
        if (statistikname !== null)
            Statistik.getInstance().pushStat(statistikname, `fach=${this.rmFilter.fach}&gruppeid=${this.rmFilter.gruppeId}`);

        // wenn Aufgabengruppierungsoption in diesem Rückmeldungstyp aktiviert ist
        const rnfAnsichtCheckbox = document.querySelector<HTMLInputElement>(`#rnf-ansicht-checkbox`);
        if (rnfAnsichtCheckbox !== null) {
            rnfAnsichtCheckbox.addEventListener('change', () => {
                this.isRnfAnsichtEnabled = rnfAnsichtCheckbox.checked;
                document.dispatchEvent(new Event('refresh-rueckmeldung'));
            });
        }
    }

}
